<template>
  <div style="padding-top: 3em;">
    <h4 class="title">Special Events Food Safety Notification Form</h4>
    <p>The Food Act 2001 and Food Safety Standards contain requirements for all food businesses (including
      not-for-profit organisations and community events). All Event Organisers conducting events in the City of Port
      Adelaide Enfield are required to complete and return the following information if you plan to serve food at your
      event.</p>
    <br />
    <div class="notification is-warning">
      <p>
        <strong>Please Note:</strong> It is a requirement under the Food Act 2001 that notification is provided to
        Council at least two (2) weeks prior to the event. If however, it cannot be demonstrated that approval has been
        granted, in the form of an FBN number, penalties will apply if trading commences. Please be advised that a Food
        Business Notification (FBN) number is required to be provided for each food stall. FBN numbers are issued by the
        Council where the temporary food stall is garaged.
      </p>
    </div>
    <br />

    <div v-if="message" class="notification is-primary">{{ message }}</div>

    <form-wizard dusk="form-wizard" @submit.prevent="onSubmit" @on-complete="onSubmit" shape="tab" color="#17C4EE" title
      subtitle ref="wizard" v-if="!message">
      <input type="hidden" name="_token" :value="csrf" />
      <input type="hidden" v-model="form" />

      <div style="padding: 1em 0px; color: #aaa;">
        <small>* Required Fields</small>
      </div>

      <!-- ************************* -->
      <!-- TAB #1 - EVENT DETAILS -->
      <!-- ************************* -->
      <tab-content title="Event Details" :before-change="validateStep1">

        
        
        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Application No. (if known)</label>
              <div class="control">
                <ApplicationSearch fieldName="developmentApplicationNumber" source="/api/application/search"
                  placeholder="eg. EVT19/0000" v-on:applicationNo="saveApplicationNo"
                  v-on:applicationNoTyping="saveApplicationNoTyping"
                  v-on:applicationNoProcessCtr="saveApplicationNoProcessCtr"></ApplicationSearch>
                <span v-if="$v.applicationNoProcessCtr.$error" class="help is-danger">Valid application number is
                  required.</span>
                <small class="help is-danger" v-if="!$v.applicationNo.EventApplicationValidator">Invalid format.</small>
                <small
                  v-if="$v.applicationNo.$invalid && !$v.applicationNo.EventApplicationValidator && applicationNoProcessCtr.length>0"
                  class="help is-danger">Invalid format.</small>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Event Name *</label>
              <div class="control">
                <input type="text" id="eventName" name="eventName" v-model="eventName"
                  :status="$v.eventName.$error ? 'error' : null" placeholder="Please enter the event name"
                  :class="{ 'input': true, 'is-danger': $v.eventName.$error }" @blur="$v.eventName.$touch()" />
                <span v-if="$v.eventName.$error && !$v.eventName.required" class="help is-danger">Event name is
                  required</span>
                <span v-if="$v.eventName.$error && !$v.eventName.AlphNumCharsValidator" class="help is-danger">Invalid
                  characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-8">
            <div class="columns" style="margin-bottom: 0px;">
              <div class="column is-3" style="padding-bottom: 0px;">
                <div class="field">
                  <label class="label">Event Date(s) *</label>
                </div>
              </div>
            </div>

            <div class="box" style="background: #fefefe; margin-top: .5em;">
              <div class="columns is-multiline" v-for="(item, index) in eventDates" :key="item.id">
                <div class="column is-10">
                  <div class="field">
                    <div class="control" style="vertical-align: middle;">
                      <div class="level">
                        <div class="level-left">
                          <datepicker :format="customFormatter" id="datepicker" :disabled-dates="disabled"
                            class="level-item" :input-class="'input'"
                            style="float: left; margin-right:1em; width: 110px;" placeholder="Select date"
                            v-model="item.date" :selected="checkDates()"></datepicker>
                          <input ref="timeField" style="width: 70px;" type="text" placeholder="hh:mm" class="level-item"
                            :class="{ 'input': true }" @blur="$v.eventDates.$touch()" v-model="item.timeFrom" />
                          <small class="level-item" style="vertical-align: -webkit-baseline-middle;">-</small>
                          <input ref="timeField" style="width: 70px;" type="text" placeholder="hh:mm" class="level-item"
                            :class="{ 'input': true }" @blur="$v.eventDates.$touch()" v-model="item.timeTo" />
                          <button class="button level-item" @click="eventDates.pop()"
                            v-if="index+1 == eventDates.length && index>0">Remove</button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span v-if="$v.eventDates.$each[index].date.$error && !$v.eventDates.$each[index].date.required"
                    class="help is-danger">Date is required</span>
                  <span
                    v-if="$v.eventDates.$each[index].timeFrom.$error && !$v.eventDates.$each[index].timeFrom.required || $v.eventDates.$each[index].timeTo.$error && !$v.eventDates.$each[index].timeTo.required"
                    class="help is-danger">Time range is required</span>
                </div>
                <div class="column is-2">
                  <div class="field has-addons is-pulled-right" v-if="index+1 == eventDates.length">
                    <p class="control">
                      <a class="button is-primary" @click="eventDates.push({date: ''})">
                        <span class="icon is-small">
                          <i class="fas fa-plus"></i>
                        </span>
                        <span>Day</span>
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div v-if="eventDateEarly" class="notification is-warning">
                <strong>Your event is within 14 days!</strong>, you will need to call Council’s Environmental Health
                Service on (08) 8405 6832 or email us on
                <a href="mailto:environmental.health@cityofpae.sa.gov.au">environmental.health@cityofpae.sa.gov.au</a>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Event Location *</label>
              <div class="control">
                <div class="select" style="vertical-align: middle;">
                  <select id="eventLocationType" name="eventLocationType" v-model="eventLocationType"
                    :class="{ 'is-danger': $v.eventLocationType.$error }" @blur="$v.eventLocationType.$touch()">
                    <option selected :value="null">--Please select--</option>
                    <option>Reserve</option>
                    <option>Street Address</option>
                  </select>
                </div>
                <span v-if="$v.eventLocationType.$error && !$v.eventLocationType.required"
                  class="help is-danger">Location is required</span>
              </div>
            </div>
          </div>
        </div>

        <template v-if="eventLocationType == 'Reserve'">
          <div class="box" style="background: #eee; padding: 1em; padding-bottom: 2em;">
            <div class="columns">
              <div class="field column is-5">
                <label class="label">
                  Reserve
                  <small class="has-text-weight-light">(Within the City of Port Adelaide Enfield)</small> *
                </label>
                <div class="control">
                  <div class="select" :class="{ 'is-danger': $v.eventLocation.$error }">
                    <select v-model="reserveName" v-on:change="saveEventLocation(reserveName)"
                      @blur="$v.eventLocation.$touch()">
                      <option selected :value="null">--Please select--</option>
                      <option>Bennett Memorial Reserve (Manningham)</option>
                      <option>Fotheringham Reserve (Osborne)</option>
                      <option>Harry Wierda Reserve (Oakden)</option>
                      <option>L J Lewis Park (Northfield)</option>
                      <option>Regency Park Reserve (Regency Park)</option>
                      <option>Semaphore Foreshore Reserve (Semaphore)</option>
                      <option>Semaphore South Foreshore (Semaphore South)</option>
                      <option>St Patricks Reserve (Alberton)</option>
                      <option>Other...</option>
                    </select>
                  </div>
                  <span v-if="$v.eventLocation.$error" class="help is-danger">Valid reserve is required.</span>
                </div>
              </div>
            </div>
            <div class="columns" v-if="reserveName=='Other...'">
              <div class="column is-4">
                <div class="field">
                  <label class="label">Name of Reserve *</label>
                  <div class="control">
                    <input type="text" placeholder="Please enter the name of the reserve"
                      :class="{ 'input': true, 'is-danger': $v.eventLocationOtherReserve.$error }"
                      @blur="$v.eventLocationOtherReserve.$touch()" name="eventLocationOtherReserve"
                      v-model="eventLocationOtherReserve" v-on:change="saveEventLocation(eventLocationOtherReserve)" />
                    <span v-if="$v.eventLocationOtherReserve.$error && !$v.eventLocationOtherReserve.required"
                      class="help is-danger">Reserve name is required</span>
                    <span
                      v-if="$v.eventLocationOtherReserve.$error && !$v.eventLocationOtherReserve.AlphNumCharsValidator"
                      class="help is-danger">Invalid characters entered</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else-if="eventLocationType == 'Street Address'">
          <div class="box" style="background: #eee;">
            <div class="columns">
              <div class="field column is-8">
                <label class="label">
                  Address of Event
                  <small class="has-text-weight-light">
                    <strong>(Select from suggested address in dropdown list)</strong>
                  </small> *
                </label>
                <div class="control">
                  <PropertyAddress fieldName="eventLocation" source="/api/address/search"
                    placeholder="eg. 1/10 Main St Semaphore" v-on:address="saveEventLocation"
                    v-on:addressTyping="saveEventLocationTyping" v-on:addressPropertyNo="saveEventLocationPropertyNo">
                  </PropertyAddress>

                  <div v-if="eventLocationPropertyNo==0 && eventLocationTyping.length>0"
                    style="background: #eee; padding: 1em;">
                    <small>
                      Please type address in full and then select correct address from the drop down list.
                      <br />eg. 1/10 Main St Semaphore SA 5091
                    </small>
                    <template v-if="$v.eventLocationPropertyNo.$error || this.eventLocationNotFound == true">
                      (
                      <input type="checkbox" id="eventLocationNotFound" v-model="eventLocationNotFound" />
                      <label for="eventLocationNotFound">
                        <small>I cannot find my address in the dropdown list.</small>
                      </label> )
                    </template>
                  </div>
                  <span v-if="$v.eventLocationPropertyNo.$error" class="help is-danger">Valid address is required.
                    (Please select from dropdown list as you type)</span>






                </div>
              </div>
              <br />
            </div>
          </div>
        </template>

        <div class="columns">
          <div class="column is-8">
            <div class="field">
              <label class="label">Event Description *</label>
              <div class="control">
                <textarea rows="8" placeholder="Please enter the description of the event"
                  :class="{ 'textarea': true, 'is-danger': $v.eventDescription.$error }"
                  @blur="$v.eventDescription.$touch()" id="eventDescription" name="eventDescription"
                  v-model="eventDescription"></textarea>
                <span v-if="$v.eventDescription.$error && !$v.eventDescription.required" class="help is-danger">Event
                  description is required</span>
                <span v-if="$v.eventDescription.$error && !$v.eventDescription.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <br />
        <br />
      </tab-content>

      <tab-content title="Contact Details" :before-change="validateStep2">
        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label">First Name *</label>
              <div class="control">
                <input type="text" autofocus id="applicantFirstName" name="applicantFirstName"
                  v-model="applicantFirstName" :status="$v.applicantFirstName.$error ? 'error' : null"
                  placeholder="Please enter the contacts first name"
                  :class="{ 'input': true, 'is-danger': $v.applicantFirstName.$error }"
                  @blur="$v.applicantFirstName.$touch()" />
                <span v-if="$v.applicantFirstName.$error && !$v.applicantFirstName.required"
                  class="help is-danger">First name is required</span>
                <span v-if="$v.applicantFirstName.$error && !$v.applicantFirstName.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>

          <div class="column is-4">
            <div class="field">
              <label class="label">Last Name *</label>
              <div class="control">
                <input type="text" autofocus id="applicantLastName" name="applicantLastName" v-model="applicantLastName"
                  :status="$v.applicantLastName.$error ? 'error' : null"
                  placeholder="Please enter the contacts last name"
                  :class="{ 'input': true, 'is-danger': $v.applicantLastName.$error }"
                  @blur="$v.applicantLastName.$touch()" />
                <span v-if="$v.applicantLastName.$error && !$v.applicantLastName.required" class="help is-danger">Last
                  name is required</span>
                <span v-if="$v.applicantLastName.$error && !$v.applicantLastName.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">Organisation *</label>
              <div class="control">
                <input type="text" autofocus id="applicantOrganisation" name="applicantOrganisation"
                  v-model="applicantOrganisation" :status="$v.applicantOrganisation.$error ? 'error' : null"
                  placeholder="Please enter the organisation name"
                  :class="{ 'input': true, 'is-danger': $v.applicantOrganisation.$error }"
                  @blur="$v.applicantOrganisation.$touch()" />
                <span v-if="$v.applicantOrganisation.$error && !$v.applicantOrganisation.required"
                  class="help is-danger">Organisation name is required</span>
                <span v-if="$v.applicantOrganisation.$error && !$v.applicantOrganisation.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

                <div class="columns">
          <div class="column is-6">
            <div class="field">
              <label class="label">ABN</label>
              <div class="control">
                <input type="text" autofocus id="applicantABN" name="applicantABN"
                  v-model="applicantABN"
                  placeholder="Please enter your ABN"
                  :class="{ 'input': true }"
                  @blur="$v.applicantABN.$touch()" />

              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-3">
            <div class="field">
              <label class="label">Contact Number *</label>
              <div class="control">
                <input type="text" id="applicantContactNumber" name="applicantContactNumber"
                  v-model="applicantContactNumber" placeholder="Please enter contact number"
                  :class="{ 'input': true, 'is-danger': $v.applicantContactNumber.$error }"
                  @blur="$v.applicantContactNumber.$touch()" />
                <span v-if="$v.applicantContactNumber.$error && !$v.applicantContactNumber.required"
                  class="help is-danger">Contact number is required</span>
                <span v-if="$v.applicantContactNumber.$error && !$v.applicantContactNumber.AlphNumCharsValidator"
                  class="help is-danger">Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-4">
            <div class="field">
              <label class="label">Email Address *</label>
              <div class="control">
                <input type="text" id="applicantEmail" name="applicantEmail" v-model="applicantEmail"
                  placeholder="Please enter email address"
                  :class="{ 'input': true, 'is-danger': $v.applicantEmail.$error }"
                  @blur="$v.applicantEmail.$touch()" />
                <span v-if="$v.applicantEmail.$error && !$v.applicantEmail.required" class="help is-danger">Email
                  address is required</span>
                <span v-if="$v.applicantEmail.$error && !$v.applicantEmail.email" class="help is-danger">Format
                  incorrect</span>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column is-8">
            <div class="field">
              <label class="label">Postal Address</label>

              <div class="box" style="background: #fefefe; margin-top: .5em;">
                <div class="columns is-multiline">
                  <div class="column is-12">
                    <div class="form-check">
                      <strong>Is the address within the City of Port Adelaide Enfield area?</strong>
                      <div class="control">
                        <label class="radio">
                          <input type="radio" name="applicantPostalAddressPAE" v-model="applicantPostalAddressPAE"
                            @blur="$v.applicantPostalAddressPAE.$touch()" value="1" />
                          Yes
                        </label>
                        <label class="radio">
                          <input type="radio" name="applicantPostalAddressPAE" v-model="applicantPostalAddressPAE"
                            @blur="$v.applicantPostalAddressPAE.$touch()" value="0" />
                          No
                        </label>

                        <label class="radio">
                          <input type="radio" name="applicantPostalAddressPAE" v-model="applicantPostalAddressPAE"
                            @blur="$v.applicantPostalAddressPAE.$touch()" value="2" />
                          PO Box
                        </label>

                        <template v-if="applicantPostalAddressPAE == 99">
                          <span v-if="$v.applicantPostalAddressPAE.$error" class="help is-danger">Please select an
                            option.</span>
                        </template>


                      </div>
                    </div>
                  </div>

                  <div class="field column is-12 test">
                    <template v-if="applicantPostalAddressPAE <= 2">
                      <template v-if="applicantPostalAddressPAE == 1">
                        <label class="label">
                          Address
                          <small class="has-text-weight-light">(Within the City of Port Adelaide Enfield)</small> *
                        </label>
                        <div class="control">
                          <PropertyAddress fieldName="applicantPostalAddress" source="/api/address/search"
                            placeholder="eg. 1/10 Main St Semaphore" v-on:address="saveApplicantPostalAddress"
                            v-on:addressTyping="saveApplicantPostalAddressTyping"
                            v-on:addressPropertyNo="saveApplicantPostalAddressPropertyNo"></PropertyAddress>
                          <span v-if="$v.applicantPostalAddress.$error" class="help is-danger">Valid address is
                            required.</span>
                        </div>
                      </template>

                      <template v-if="applicantPostalAddressPAE == 0">
                        <label class="label">
                          Address
                          <small class="has-text-weight-light">(Outside of the City of Port Adelaide Enfield)</small> *
                        </label>
                        <div class="control">
                          <GoogleAddressAutocomplete fieldName="incidentAddressOther"
                            placeholder="eg. 1/10 Main St Semaphore" v-on:address="saveApplicantPostalAddress"
                            v-on:addressPropertyNo="saveApplicantPostalAddressPropertyNo"></GoogleAddressAutocomplete>
                          <span v-if="$v.applicantPostalAddress.$error" class="help is-danger">Valid address is
                            required.</span>
                        </div>
                      </template>

                      <template v-if="applicantPostalAddressPAE == 2">
                        <label class="label">PO Box *</label>
                        <div class="control">
                          <input type="text" placeholder="Please enter the PO Box number"
                            :class="{ 'input': true, 'is-danger': $v.applicantPostalAddress.$error }"
                            @blur="$v.applicantPostalAddress.$touch()" name="applicantPostalAddress"
                            v-model="applicantPostalAddress" />
                          <span v-if="$v.applicantPostalAddress.$error && !$v.applicantPostalAddress.required"
                            class="help is-danger">PO Box is required</span>
                        </div>
                      </template>


                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <br />
        <br />
      </tab-content>

      <tab-content title="Food Stall Details" :before-change="validateStep3">
        <div class="columns">
          <div class="column is-8">
            <div class="columns" style="margin-bottom: 0px;">
              <div class="column is-3" style="padding-bottom: 0px;">
                <div class="field">
                  <label class="label">Stall Detail(s) *</label>
                </div>
              </div>
            </div>

            <div class="box" style="background: #fefefe; margin-top: .5em;">
              <template v-for="(item, index) in stalls">
                <div class="columns is-multiline" :key="index">
                  <div class="column is-10">
                    <hr v-if="index>0" />
                    <h4 class="title">#{{ index+1 }}</h4>

                    <div class="field">
                      <!-- <label class="label">Postal Address</label> -->
                      <!-- <div class="box" style="background: #fefefe; margin-top: .5em;"> -->
                      <div class="columns is-multiline">
                        <div class="column is-12">
                          <div class="form-check">
                            <span>Is the business (food stall holder) address within the City of Port Adelaide Enfield
                              area?</span>
                            <div class="control">
                              <label class="radio">
                                <input type="radio" v-model="item.proprietorAddressPAE" @blur="$v.stalls.$touch()"
                                  @change="clearProprietorAddress(index)" value="1" />
                                Yes
                              </label>
                              <label class="radio">
                                <input type="radio" v-model="item.proprietorAddressPAE" @blur="$v.stalls.$touch()"
                                  @change="clearProprietorAddress(index)" value="0" />
                                No
                              </label>

                              <template v-if="item.proprietorAddressPAE == 2">
                                <span v-if="$v.stalls.$each[index].proprietorAddressPAE.$error"
                                  class="help is-danger">Please select an option.</span>
                              </template>
                            </div>
                          </div>
                        </div>

                        <div class="field column is-12" style="padding-top:0;">
                          <template v-if="item.proprietorAddressPAE <= 1">
                            <template v-if="item.proprietorAddressPAE == 1">
                              <label class="label">
                                Address
                                <small class="has-text-weight-light">(Within the City of Port Adelaide Enfield)</small>
                                *
                              </label>
                              <div class="control">
                                <PropertyAddress fieldName="ProprietorAddress" source="/api/address/search"
                                  placeholder="eg. 1/10 Main St Semaphore"
                                  v-on:address="saveProprietorAddress($event, index)"
                                  v-on:addressTyping="saveProprietorAddressTyping($event, index)"
                                  v-on:addressPropertyNo="saveProprietorPropertyNo($event, index)"></PropertyAddress>
                                <span v-if="$v.stalls.$each[index].proprietorAddress.$error"
                                  class="help is-danger">Valid address is required.</span>
                              </div>
                            </template>

                            <template v-if="item.proprietorAddressPAE == 0">
                              <label class="label">
                                Address
                                <small class="has-text-weight-light">(Outside of the City of Port Adelaide
                                  Enfield)</small> *
                              </label>
                              <div class="control">
                                <GoogleAddressAutocomplete fieldName="proprietorAddressOther"
                                  placeholder="eg. 1/10 Main St Semaphore"
                                  v-on:address="saveProprietorAddress($event, index)"
                                  v-on:addressPropertyNo="saveProprietorPropertyNo($event, index)">
                                </GoogleAddressAutocomplete>
                                <span v-if="$v.stalls.$each[index].proprietorAddress.$error"
                                  class="help is-danger">Valid address is required.</span>
                              </div>
                            </template>
                          </template>
                        </div>
                      </div>

                      <!-- </div> -->
                    </div>

                    <div class="field">
                      <label class="label">Name of business (food stall holder) *</label>
                      <div class="control" style="vertical-align: middle;">
                        <input style type="text" placeholder="Please enter name of proprietor"
                          v-model="item.NameProprietor" :class="{ 'input': true }" @blur="$v.stalls.$touch()" />
                      </div>
                      <span
                        v-if="$v.stalls.$each[index].NameProprietor.$error && !$v.stalls.$each[index].NameProprietor.required"
                        class="help is-danger">Name is required</span>
                    </div>

                    <div class="field">
                      <label class="label">Trading name *</label>
                      <div class="control" style="vertical-align: middle;">
                        <input style type="text" placeholder="Please enter the trading name of proprietor"
                          v-model="item.TradingName" :class="{ 'input': true }" @blur="$v.stalls.$touch()" />
                      </div>
                      <span
                        v-if="$v.stalls.$each[index].TradingName.$error && !$v.stalls.$each[index].TradingName.required"
                        class="help is-danger">Trading name is required</span>
                    </div>

                    <div class="field">
                      <label class="label">Type of food being served *</label>
                      <div class="control" style="vertical-align: middle;">
                        <input style type="text" placeholder="Please enter the type of food being served"
                          v-model="item.FoodType" :class="{ 'input': true }" @blur="$v.stalls.$touch()" />
                      </div>
                      <span v-if="$v.stalls.$each[index].FoodType.$error && !$v.stalls.$each[index].FoodType.required"
                        class="help is-danger">Type of food is required</span>
                    </div>
                    <div class="field">
                      <label class="label">Contact name *</label>
                      <div class="control" style="vertical-align: middle;">
                        <input style type="text" placeholder="Please enter contact person" v-model="item.ContactPerson"
                          :class="{ 'input': true }" @blur="$v.stalls.$touch()" />
                      </div>
                      <span
                        v-if="$v.stalls.$each[index].ContactPerson.$error && !$v.stalls.$each[index].ContactPerson.required"
                        class="help is-danger">Contact person is required</span>
                    </div>

                    <div class="field">
                      <label class="label">Contact number *</label>
                      <div class="control" style="vertical-align: middle;">
                        <input style type="text" placeholder="Please enter contact number" v-model="item.ContactNumber"
                          :class="{ 'input': true }" @blur="$v.stalls.$touch()" />
                      </div>
                      <span
                        v-if="$v.stalls.$each[index].ContactNumber.$error && !$v.stalls.$each[index].ContactNumber.required"
                        class="help is-danger">Contact number is required</span>
                    </div>

                    <div class="field">
                      <label class="label">Email address *</label>
                      <div class="control" style="vertical-align: middle;">
                        <input style type="text" placeholder="Please enter email address" v-model="item.EmailAddress"
                          :class="{ 'input': true }" @blur="$v.stalls.$touch()" />
                      </div>
                      <span
                        v-if="$v.stalls.$each[index].EmailAddress.$error && !$v.stalls.$each[index].EmailAddress.required"
                        class="help is-danger">Email address is required</span>
                        <span v-if="$v.stalls.$each[index].EmailAddress.$error && !$v.stalls.$each[index].EmailAddress.email" class="help is-danger">Format
                  incorrect</span>
                    </div>

                    <div class="field">
                      <label class="label">Max number of food handlers at the stall *</label>
                      <div class="control">
                        <div class="level">
                          <div class="level-left">
                            <input type="text" placeholder="0" style="width: 80px;" class="level-item"
                              :class="{ 'input': true }" @blur="$v.stalls.$touch()"
                              v-model="item.MaxNumberFoodHandlers" />
                          </div>
                        </div>
                      </div>
                      <span
                        v-if="$v.stalls.$each[index].MaxNumberFoodHandlers.$error && !$v.stalls.$each[index].MaxNumberFoodHandlers.required"
                        class="help is-danger">Number of food handlers is required</span>
                    </div>

                    <div class="field">
                      <label class="label">
                        FBN
                        <small class="has-text-weight-light">(FBN is your food business notification number that has
                          been provided to you by your Council when you formally notified that you are operating a food
                          business.)</small> *
                      </label>
                      <div class="control">
                        <input type="text" placeholder="Please enter FBN" style="width: 280px;"
                          :class="{ 'input': true }" @blur="$v.stalls.$touch()" v-model="item.FBN" />
                      </div>
                      <span v-if="$v.stalls.$each[index].FBN.$error && !$v.stalls.$each[index].FBN.required"
                        class="help is-danger">FBN is required</span>
                    </div>


                    <!-- ADD FILE DROP HERE -->
                    <div class="field">
                      <fieldset style="border: 1px solid #333; padding: 1rem;">
                        <legend class="label" style="padding:.6rem;">Food Business Council Notification Document</legend>
                        <p>(FBN document is the food certificate provided to you by your council, when you formally notified that you are operating a food business)</p>
                        <FileDragDropInstant v-on:fileIDs="appendFileIDs" v-on:removeFileID="removeFileID"></FileDragDropInstant>
                      </fieldset>
                    </div>
                    <div class="field">
                      <fieldset style="border: 1px solid #333; padding: 1rem;">
                        <legend class="label" style="padding:.6rem;">Food Vendor Public Liability Insurance Certificate </legend>
                        <FileDragDropInstant v-on:fileIDs="appendFileIDs" v-on:removeFileID="removeFileID"></FileDragDropInstant>
                      </fieldset>
                    </div>


                    <div class="field">
                      <label class="label">Garaging Council *</label>
                      <div class="control">
                        <div class="select" style="vertical-align: middle;">
                          <select v-model="item.GaragingCouncil" :class="{ }" @blur="$v.stalls.$touch()">
                            <option selected :value="null">--Please Select Garaging Council--</option>
                            <template v-for="(item) in councilList">
                              <optgroup :label="item.region" :key="item.id">
                                <option v-for="(item) in item.councils" :key="item.id">{{ item.name }}</option>
                              </optgroup>
                            </template>
                          </select>
                        </div>
                      </div>
                      <span
                        v-if="$v.stalls.$each[index].GaragingCouncil.$error && !$v.stalls.$each[index].GaragingCouncil.required"
                        class="help is-danger">Garaging Council is required</span>
                    </div>

                  </div>
                </div>
                <div class="columns" :key="item.id">
                  <div class="column is-2">
                    <div class="field has-addons" v-if="index+1 == stalls.length">
                      <p class="control">
                        <a class="button is-primary" @click="stalls.push(
                          {
                            NameProprietor: '',
                            proprietorAddressPAE: 2,
                            proprietorAddress: '',
                            proprietorAddressTyping: '',
                            proprietorPropertyNo: '',
                            FoodType: '',
                            ContactPerson: '',
                            ContactNumber: '',
                            MaxNumberFoodHandlers: '',
                            FBN: '',
                            GaragingCouncil: null,
                          }
                          )">
                          <span class="icon is-small">
                            <i class="fas fa-plus"></i>
                          </span>
                          <span>Stall</span>
                        </a>
                      </p>&nbsp;
                      <button class="button" @click="stalls.pop()"
                        v-if="index+1 == stalls.length && index>0">Remove</button>
                    </div>
                  </div>
                </div>


              </template>
            </div>
          </div>
        </div>

        <div>
          <a target="_blank" href="https://www.cityofpae.sa.gov.au/?a=1272780">Food event checklist</a><br/>
          <a target="_blank" href="https://www.cityofpae.sa.gov.au/?a=413025">Food event guidelines</a>
        </div>


        <br />
        <br />
      </tab-content>

      <div class="column is-8" v-if="enableGoogleCaptch">
        <div class="field">
          <vue-recaptcha ref="invisibleRecaptcha" @verify="onVerify" @expired="onExpired" size="invisible"
            :sitekey="googleCaptchaKey"></vue-recaptcha>
        </div>
      </div>

      <div class="modal" v-bind:class="{'is-active' : loading}">
        <div class="modal-background"></div>
        <div class="modal-content has-text-centered">
          <h1 class="title is-3 has-text-light">Please wait...</h1>
          <button class="button is-info is-loading is-fullwidth is-large"
            style="font-size: 5em; background: transparent;">Submitting</button>
        </div>
      </div>

      <hr />
    </form-wizard>
  </div>
</template>
<script>
const isDate = value => moment(value, "DD/MM/YYYY", true).isValid();

import VueRecaptcha from "vue-recaptcha";

import ApplicationSearch from "../../../../../../resources/assets/js/components/address/Typeahead-Applications.vue";
import PropertyAddress from "../../../../../../resources/assets/js/components/address/Typeahead-CouncilAddress.vue";
import GoogleAddressAutocomplete from "../../../../../../resources/assets/js/components/address/GoogleAddressAutocomplete.vue";
import FileDragDropInstant from "../../../../../../Modules/FileManagement/Resources/assets/js/components/FileDragDropInstant.vue";


import {
    alpha,
    required,
    requiredIf,
    email,
    numeric,
    helpers,
    alphaNum,
    minValue,
    maxValue,
    minLength
} from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
// Custom Validators
import { TimeValidator } from "../../../../../../resources/assets/js/validators/time-validator";
import { AlphNumCharsValidator } from "../../../../../../resources/assets/js/validators/alpha-num-chars-validator";
import { EventApplicationValidator } from "../../../../../../resources/assets/js/validators/event-application-validator";
import { MinDate } from "../../../../../../resources/assets/js/validators/min-date";

const validEventApplicationNumber = value =>
    !helpers.req(value) || value.indexOf("cool") >= 0;

import moment from "moment";

import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";

import datepicker from "vuejs-datepicker";

export default {
    components: {
        PropertyAddress,
        ApplicationSearch,
        GoogleAddressAutocomplete,
        FileDragDropInstant,
        VueRecaptcha,
        FormWizard,
        TabContent,
        datepicker
    },
    props: {
        form: {}
    },
    data() {
        return {
            today: moment(new Date()).format("DD/MM/YYYY"),
            googleCaptchaKey: "6LcyrE0UAAAAAExxoj3NBm1vA-WMu88CIigyzkBU",
            enableGoogleCaptch: false,
            csrf: "",
            formTitle: this.form,
            message: "",
            submitted: false,
            loading: false,
            fileIDs: [],
            format: "dd/MM/yyyy",
            disabled: {
                to: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth(),
                    new Date().getDate()
                )
            },
            //EventFields
            applicationNo: "",
            applicationNoTyping: "",
            applicationNoProcessCtr: 0,
            eventName: "",
            eventDates: [
                {
                    date: "",
                    timeFrom: "",
                    timeTo: ""
                }
            ],
            eventDateEarly: false,
            eventLocationType: null,
            eventLocation: "",
            eventLocationTyping: "",
            eventLocationOtherReserve: "",
            eventLocationPropertyNo: "",
            eventLocationNotFound: false,
            reserveName: null,
            eventDescription: "",
            //ContactFields
            applicantFirstName: "",
            applicantLastName: "",
            applicantOrganisation: "",
            applicantABN: "",
            applicantContactNumber: "",
            applicantPostalAddressPAE: 99,
            applicantPostalAddress: "",
            applicantPostalAddressTyping: "",
            applicantPostalAddressPropertyNo: "",
            applicantEmail: "",
            //StallFields
            stalls: [
                {
                    NameProprietor: "",
                    TradingName: "",
                    proprietorAddressPAE: 2,
                    proprietorAddress: "",
                    proprietorAddressTyping: "",
                    proprietorPropertyNo: "",
                    FoodType: "",
                    ContactPerson: "",
                    ContactNumber: "",
                    EmailAddress: "",
                    MaxNumberFoodHandlers: "",
                    FBN: "",
                    GaragingCouncil: null
                }
            ],

            councilList: [
                {
                    region: "Other",
                    councils: [
                        {
                            name: "Interstate"
                        },
                        {
                            name: "International"
                        }
                    ]
                },
                {
                    region: "Metropolitan",
                    councils: [
                        {
                            name: "City of Adelaide"
                        },
                        {
                            name: "Adelaide Hills Council"
                        },
                        {
                            name: "City of Burnside"
                        },
                        {
                            name: "City of Campbelltown"
                        },
                        {
                            name: "City of Charles Sturt"
                        },
                        {
                            name: "Town of Gawler"
                        },
                        {
                            name: "City of Holdfast Bay"
                        },
                        {
                            name: "City of Marion"
                        },
                        {
                            name: "City of Mitcham"
                        },
                        {
                            name: "City of Norwood Payneham & St Peters"
                        },
                        {
                            name: "City of Onkaparinga"
                        },
                        {
                            name: "City of Playford"
                        },
                        {
                            name: "City of Port Adelaide Enfield"
                        },
                        {
                            name: "City of Prospect"
                        },
                        {
                            name: "City of Salisbury"
                        },
                        {
                            name: "City of Tea Tree Gully"
                        },
                        {
                            name: "City of Unley"
                        },
                        {
                            name: "Town of Walkerville"
                        },
                        {
                            name: "City of West Torrens"
                        }
                    ]
                },
                {
                    region: "Eyre Peninsula",
                    councils: [
                        {
                            name: "District Council of Ceduna"
                        },
                        {
                            name: "District Council of Cleve"
                        },
                        {
                            name: "District Council of Elliston"
                        },
                        {
                            name: "District Council of Franklin Harbour"
                        },
                        {
                            name: "District Council of Kimba"
                        },
                        {
                            name: "District Council of Lower Eyre Peninsula"
                        },
                        {
                            name: "City of Port Augusta"
                        },
                        {
                            name: "City of Port Lincoln"
                        },
                        {
                            name: "District Council of Streaky Bay"
                        },
                        {
                            name: "District Council of Tumby Bay"
                        },
                        {
                            name: "Wudinna District Council"
                        },
                        {
                            name: "City of Whyalla"
                        }
                    ]
                },
                {
                    region: "Central Region",
                    councils: [
                        {
                            name: "Adelaide Plains Council"
                        },
                        {
                            name: "Barossa Council"
                        },
                        {
                            name: "District Council of Barunga West"
                        },
                        {
                            name:
                                "District Council of Clare and Gilbert Valleys"
                        },
                        {
                            name: "Copper Coast Council"
                        },
                        {
                            name: "Flinders Ranges Council"
                        },
                        {
                            name: "Regional Council of Goyder"
                        },
                        {
                            name: "Light Regional Council"
                        },
                        {
                            name: "District Council of Mount Remarkable"
                        },
                        {
                            name: "Northern Areas Council"
                        },
                        {
                            name: "District Council of Orroroo Carrieton"
                        },
                        {
                            name: "District Council of Peterborough"
                        },
                        {
                            name: "Port Pirie Regional Council"
                        },
                        {
                            name: "Wakefield Regional Council"
                        },
                        {
                            name: "Yorke Peninsula Council"
                        }
                    ]
                },
                {
                    region: "Southern and Hills Region",
                    councils: [
                        {
                            name: "Alexandrina Council"
                        },
                        {
                            name: "Kangaroo Island Council"
                        },
                        {
                            name: "District Council of Mount Barker"
                        },
                        {
                            name: "City of Victor Harbor"
                        },
                        {
                            name: "District Council of Yankalilla"
                        }
                    ]
                },
                {
                    region: "Murray Mallee Region",
                    councils: [
                        {
                            name: "Berri Barmera Council"
                        },
                        {
                            name: "The Coorong District Council"
                        },
                        {
                            name: "Gerard Community Council"
                        },
                        {
                            name: "District Council of Karoonda East Murray"
                        },
                        {
                            name: "District Council of Loxton Waikerie"
                        },
                        {
                            name: "Mid Murray Council"
                        },
                        {
                            name: "Rural City of Murray Bridge"
                        },
                        {
                            name: "Renmark Paringa Council"
                        },
                        {
                            name: "Southern Mallee District Council"
                        }
                    ]
                },
                {
                    region: "Southeast Region",
                    councils: [
                        {
                            name: "District Council of Grant"
                        },
                        {
                            name: "Kingston District Council"
                        },
                        {
                            name: "City of Mount Gambier"
                        },
                        {
                            name: "Naracoorte Lucindale Council"
                        },
                        {
                            name: "District Council of Robe"
                        },
                        {
                            name: "Tatiara District Council"
                        },
                        {
                            name: "Wattle Range Council"
                        }
                    ]
                },
                {
                    region: "Outback",
                    councils: [
                        {
                            name: "Anangu Pitjantjatjara Yankunytjatjara"
                        },
                        {
                            name: "District Council of Coober Pedy"
                        },
                        {
                            name: "Maralinga Tjarutja"
                        },
                        {
                            name: "Nepabunna Community Council"
                        },
                        {
                            name: "Outback Communities Authority"
                        },
                        {
                            name: "Municipal Council of Roxby Downs"
                        }
                    ]
                }
            ]
        };
    },
    validations: {
        //TAB1
        applicationNo: {
            EventApplicationValidator: requiredIf(function() {
                return this.applicationNoTyping != "";
            }),
            required: requiredIf(function() {
                return this.applicationNoTyping != "";
            })
        },
        applicationNoProcessCtr: {
            required: requiredIf(function() {
                return this.applicationNoTyping != "";
            }),
            minValue: minValue(0)
        },
        eventName: { required, AlphNumCharsValidator },
        eventDates: {
            $each: {
                date: { required },
                timeFrom: { required },
                timeTo: { required }
            }
        },
        eventLocationType: { required },
        eventLocation: {
            required: requiredIf(function() {
                return (
                    this.eventLocationType == "Street Address" &&
                    this.eventLocationNotFound == false
                );
            }),
            AlphNumCharsValidator,
            minLength: minLength(1)
        },
        eventLocationOtherReserve: {
            required: requiredIf(function() {
                return this.reserveName == "Other...";
            })
        },
        eventLocationPropertyNo: {
            required: requiredIf(function() {
                return (
                    this.eventLocationType == "Street Address" &&
                    this.eventLocationNotFound == false
                );
            })
        },

        eventDescription: {
            required,
            AlphNumCharsValidator,
            minLength: minLength(1)
        },

        formStep1: [
            "applicationNo",
            "applicationNoProcessCtr",
            "eventName",
            "eventDates",
            "eventLocationType",
            "eventLocation",
            "eventLocationOtherReserve",
            "eventLocationPropertyNo",
            "eventDescription"
        ],

        //TAB2
        applicantFirstName: { required, AlphNumCharsValidator },
        applicantLastName: { required, AlphNumCharsValidator },
        applicantOrganisation: { required, AlphNumCharsValidator },
        applicantContactNumber: { required, AlphNumCharsValidator },
        applicantPostalAddressPAE: { required, maxValue: maxValue(2) },
        applicantPostalAddress: { required },
        applicantEmail: { required, email },

        formStep2: [
            "applicantFirstName",
            "applicantLastName",
            "applicantOrganisation",
            "applicantContactNumber",
            "applicantPostalAddressPAE",
            "applicantPostalAddress",
            "applicantEmail"
        ],

        //TAB3
        stalls: {
            $each: {
                NameProprietor: { required, AlphNumCharsValidator },
                TradingName: { required, AlphNumCharsValidator },
                proprietorAddressPAE: { required, maxValue: maxValue(1) },
                proprietorAddress: { required },
                FoodType: { required },
                ContactPerson: { required },
                ContactNumber: { required },
                EmailAddress: { required, email },
                MaxNumberFoodHandlers: { required, minValue: minValue(1) },
                FBN: { required },
                GaragingCouncil: { required }
            }
        },
        formStep3: ["stalls"]
    },
    watch: {
        message: function(val) {
            if (!val === "") {
                this.loading = false;
            }
        },
        applicantPostalAddressPAE: function(val) {
            if (val == 2) {
                this.clearApplicantPostalAddress();
            } else if (val == 1) {
                this.applicantPostalAddressPAE = 1;
                this.clearApplicantPostalAddress();
            } else {
                this.applicantPostalAddressPAE = 0;
                this.clearApplicantPostalAddress();
            }
        },
        eventLocationType: function(val) {
            this.clearEventLocation();
        }
    },
    computed: {
        fullName: function() {
            return this.applicantFirstName + " " + this.applicantLastName;
        },
        precis: function() {
            return (
                "Food Safety Notification - " +
                this.eventName +
                " " +
                moment(this.eventDates[0].date).format("DD/MM/YYYY") +
                " " +
                this.applicantOrganisation
            );
        },
        formData: function() {
            var fields = {};
            fields.fileIDs = this.fileIDs;
            //TAB1
            fields.eventName = this.eventName;
            fields.eventDates = this.eventDates;
            fields.applicationNo = this.applicationNo;
            fields.applicationNoProcessCtr = this.applicationNoProcessCtr;

            fields.eventLocationType = this.eventLocationType;

            if (this.eventLocationType == "Reserve") {
                fields.eventLocation = this.eventLocation;
            } else {
                fields.eventLocation =
                    this.eventLocationPropertyNo > 0
                        ? this.eventLocation
                        : this.eventLocationTyping;
            }

            fields.eventLocationPropertyNo =
                this.eventLocationPropertyNo > 0
                    ? this.eventLocationPropertyNo
                    : 0;

            fields.eventDescription = this.eventDescription;

            //TAB2
            fields.applicantFirstName = this.applicantFirstName;
            fields.applicantLastName = this.applicantLastName;
            fields.applicantOrganisation = this.applicantOrganisation;
            fields.applicantABN = this.applicantABN;
            fields.applicantContactNumber = this.applicantContactNumber;
            fields.applicantEmail = this.applicantEmail;

            if (
                this.applicantPostalAddressPropertyNo >= 0 ||
                this.applicantPostalAddressPAE == 2
            ) {
                fields.applicantPostalAddress = this.applicantPostalAddress;
            } else {
                fields.applicantPostalAddress = this.applicantPostalAddressTyping;
            }

            fields.applicantPostalAddressPropertyNo =
                this.applicantPostalAddressPropertyNo > 0
                    ? this.applicantPostalAddressPropertyNo
                    : 0;

            //TAB3
            fields.stalls = this.stalls;
            this.stalls.forEach(function(stall, index) {
                if (!stall.proprietorPropertyNo > 0) {
                    fields.stalls[index].proprietorAddress =
                        stall.proprietorAddressTyping;
                }
            });

            fields.precis = this.precis;
            fields.subject = "11.71.2.22";

            var actions = [];
            actions.push("A11.71.2.37");
            //The action ID is A16.6.1.9.
            fields.action = actions;

            return JSON.stringify(fields);
        }
    },
    mounted() {
        this.csrf = window.laravel.csrfToken;
    },
    methods: {
        scrollToTop() {
            window.scrollTo(0, 300);
        },

        // FILE ATTACHMENT HANDLING
        saveFileIDs(value) {
            this.fileIDs = value;
        },
        appendFileIDs(value) {
            let self = this;
            value.forEach(function(v) {
                if (self.fileIDs.indexOf(v) < 0) {
                    self.fileIDs.push(v);
                }
            });
        },
        removeFileID(value) {
            this.fileIDs.splice(this.fileIDs.indexOf(value), 1);
        },

        // STEP VALIDATION
        validateStep1() {
            this.$v.formStep1.$touch();
            this.scrollToTop();
            return !this.$v.formStep1.$invalid;
        },
        validateStep2() {
            this.$v.formStep2.$touch();
            this.scrollToTop();
            return !this.$v.formStep2.$invalid;
        },
        validateStep3() {
            this.$v.formStep3.$touch();
            this.scrollToTop();
            return !this.$v.formStep3.$invalid;
        },

        onSubmit() {
            if (this.validateStep3()) {
                if (this.enableGoogleCaptch) {
                    this.$refs.invisibleRecaptcha.execute();
                } else {
                    this.submitForm();
                }
            }
        },
        submitForm() {
            this.loading = true;
            axios
                .post("/onlineform/" + this.form, {
                    _token: this.csrf,
                    formTitle: this.formTitle,
                    fullName: this.fullName,
                    emailAddress: this.applicantEmail,
                    contactNumber: this.applicantContactNumber,
                    precis: this.precis,
                    formData: this.formData,
                    message: ""
                })
                .then(response => {
                    console.log(response.data.message);
                    this.message = response.data.message;
                    this.clear();
                    //this.submitted = true
                    setTimeout(() => (this.submitted = true), 5000);
                    this.loading = false;
                });
        },
        clear() {},

        // APPLICANT ADDRESS
        saveApplicantPostalAddress(value) {
            this.applicantPostalAddress = value;
        },
        saveApplicantPostalAddressTyping(value) {
            this.applicantPostalAddressTyping = value;
        },
        saveApplicantPostalAddressPropertyNo(value) {
            this.applicantPostalAddressPropertyNo = value;
        },
        clearApplicantPostalAddress() {
            this.applicantPostalAddress = "";
            this.applicantPostalAddressPropertyNo = "";
        },

        // APPLICATION NUMBER
        saveApplicationNo(value) {
            this.applicationNo = value;
        },
        saveApplicationNoTyping(value) {
            this.applicationNo = "";
            this.applicationNoTyping = value;
            //this.$set(this.applicationNoTyping, value);
        },
        saveApplicationNoProcessCtr(value) {
            this.applicationNoProcessCtr = value;
        },

        // EVENT ADDRESS
        saveEventLocation(value) {
            if (value == "null") {
                this.eventLocation = null;
            } else {
                this.eventLocation = value;
                if (this.reserveName != "Other...") {
                    this.eventLocationOtherReserve = "";
                }
            }
        },
        saveEventLocationTyping(value) {
            this.eventLocationTyping = value;
        },

        saveEventLocationPropertyNo(value) {
            this.eventLocationPropertyNo = value;
        },
        clearEventLocation() {
            this.eventLocation = "";
            this.eventLocationPropertyNo = "";
        },

        // PROPRIETOR ADDRESS
        saveProprietorAddress: function(event, index) {
            this.stalls[index].proprietorAddress = event;
        },
        saveProprietorAddressTyping: function(event, index) {
            this.stalls[index].proprietorAddressTyping = event;
        },
        saveProprietorPropertyNo(event, index) {
            this.stalls[index].proprietorPropertyNo = event;
        },
        clearProprietorAddress(index) {
            this.stalls[index].proprietorAddress = "";
            this.stalls[index].proprietorPropertyNo = "";
        },

        // DATE PICKER
        customFormatter(date) {
            return moment(date).format("DD/MM/YYYY");
        },

        checkDates() {
            const currentDate = moment(new Date()).startOf("day");
            for (var item in this.eventDates) {
                const selectedDate = moment(
                    this.eventDates[item].date,
                    "DD.MM.YYYY",
                    true
                );
                const difference = selectedDate.diff(currentDate, "days");
                if (difference <= 14) {
                    return (this.eventDateEarly = true);
                }
            }
            this.eventDateEarly = false;
        }
    }
};
</script>



// WEBPACK FOOTER //
// Modules/OnlineForm/Resources/assets/js/components/LayoutFoodSafetyNotification.vue